<template>
  <div>
    <BlBackdrop
      v-if="!currentPasswordVerified"
      title="Confirm your password"
      description="To verify your identity before resetting the team member's password, please enter your own password."
      label="Confirm"
      @backdrop-close="$emit('close-modal')"
      :submitLoading="submitLoading"
      :disabled="isDisabledSubmit"
      @backdrop-action="onVerifyPassword"
    >
      <div class="flex flex-col gap-3 px-8">
        <BlInputText :input="verifyPasswordInput" @changed="inputHandler" />
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="currentPasswordVerified"
      title="Reset password"
      :description="
        `Choose how to set the new password for ${user.name} (${user.email}).`
      "
      label="Reset"
      :submitLoading="submitLoading"
      :disabled="isDisabledSubmit"
      @backdrop-close="$emit('close-modal')"
      @backdrop-action="onSubmit"
    >
      <div class="flex flex-col gap-3 px-8">
        <div
          class="flex gap-4 cursor-pointer"
          @click="passwordAutogenerated = true"
        >
          <BlInputRadio :checked="passwordAutogenerated" name="autogenerated" />
          <div class="flex flex-col gap-1">
            <BlTextSubtitle3 text="Autogenerated password" />
            <BlTextBody2
              text="You can view the password after you create the user."
            />
          </div>
        </div>
        <div
          class="flex gap-4 cursor-pointer"
          @click="passwordAutogenerated = false"
        >
          <BlInputRadio
            :checked="!passwordAutogenerated"
            name="autogenerated"
          />
          <div class="flex flex-col gap-1">
            <BlTextSubtitle3 text="Custom password" />
            <BlTextBody2 text="Enter a custom password for the user." />
          </div>
        </div>

        <div class="ml-8 flex flex-col gap-4">
          <BlInputText :input="customPasswordInput" @changed="inputHandler" />
          <div class="grid grid-cols-3">
            <div class="flex items-center gap-2">
              <BlIcon
                icon="check"
                :color="'secondary'"
                class="rounded-full w-6 h-6"
                :class="[passwordHasSixChar ? 'bg-success-400' : 'bg-grey-300']"
              />
              <div class="text-xs">6 characters minimum</div>
            </div>
            <div class="flex items-center gap-2">
              <BlIcon
                icon="check"
                :color="'secondary'"
                :class="[
                  passwordHasUppercase ? 'bg-success-400' : 'bg-grey-300'
                ]"
                class="rounded-full w-6 h-6"
              />
              <div class="text-xs">1 uppercase character</div>
            </div>
          </div>
          <div class="grid grid-cols-3">
            <div class="flex items-center gap-2">
              <BlIcon
                icon="check"
                :color="'secondary'"
                :class="[
                  passwordHasLowescase ? 'bg-success-400' : 'bg-grey-300'
                ]"
                class="rounded-full w-6 h-6"
              />
              <div class="text-xs">1 lowercase character</div>
            </div>
            <div class="flex items-center gap-2">
              <BlIcon
                icon="check"
                :color="'secondary'"
                class="rounded-full w-6 h-6"
                :class="[passwordHasNumber ? 'bg-success-400' : 'bg-grey-300']"
              />
              <div class="text-xs">1 number</div>
            </div>
          </div>
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="loginDetailsOpened"
      title="Send login details"
      description="Success! The new password is now available, and you can send it to the member via email. Please make sure to save this information, as it will not be accessible again once the pop-up is closed."
      :footer="false"
    >
      <div class="flex flex-col gap-4 pb-8 px-8">
        <div class="py-4 px-6 flex flex-col gap-4 rounded-3xl bg-slate-100">
          <div
            v-if="loginDetails.status === 'pending'"
            class="grid grid-cols-12 items-center"
          >
            <BlTextBody2 class="col-span-2" text="Sign in URL" />
            <div class="col-span-10 flex items-center gap-2">
              <BlInputButton
                type="plain"
                icon="copy"
                @clicked="
                  copyToClipboard(
                    `${origin}/setup-account/${loginDetails.signInToken}`,
                    'Sign in URL'
                  )
                "
              />
              <BlTextBody2
                class="text-slate-1000 font-medium"
                :text="`${origin}/setup-account/${loginDetails.signInToken}`"
              />
            </div>
          </div>
          <div class="grid grid-cols-12 items-center">
            <BlTextBody2 class="col-span-2" text="Email" />
            <div class="col-span-10 flex items-center gap-2">
              <BlInputButton
                type="plain"
                icon="copy"
                @clicked="copyToClipboard(loginDetails.email, 'Email')"
              />
              <BlTextBody2
                class="text-slate-1000 font-medium"
                :text="loginDetails.email"
              />
            </div>
          </div>
          <div class="grid grid-cols-12 items-center">
            <BlTextBody2 class="col-span-2" text="Password" />
            <div class="col-span-10 flex items-center gap-2">
              <BlInputButton
                type="plain"
                icon="copy"
                @clicked="copyToClipboard(loginDetails.password, 'Password')"
              />
              <BlTextBody2
                v-if="!showPassword"
                :text="loginDetails.password.replace(/[\S]/g, '*')"
                class="text-slate-1000 font-medium"
              />
              <BlTextBody2
                v-if="showPassword"
                :text="loginDetails.password"
                class="text-slate-1000 font-medium"
              />
              <BlInputButton
                type="plain"
                :label="showPassword ? 'Hide' : 'Show'"
                dense
                @clicked="showPassword = !showPassword"
              />
            </div>
          </div>
        </div>

        <div class="flex items-center justify-end gap-4">
          <BlInputButton
            type="plain"
            label="Close"
            @clicked="$emit('close-modal')"
          />
          <BlInputButton
            type="outlined"
            label="Download details"
            :loading="downloadLoading"
            @clicked="onDownloadDetails"
          />
          <BlInputButton label="Send via email" @clicked="onSendViaEmail" />
        </div>
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import BlBackdrop from "../BlBackdrop.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlInputText from "../BlInputText.vue";
import BlInputRadio from "../BlInputRadio.vue";
import BlInputButton from "../BlInputButton.vue";
import BlIcon from "../BlIcon.vue";

export default {
  name: "ResetUserPasswordModal",
  components: {
    BlBackdrop,
    BlTextSubtitle3,
    BlTextBody2,
    BlInputText,
    BlInputRadio,
    BlInputButton,
    BlIcon
  },
  props: {
    user: { type: Object, required: true }
    // @close-modal
  },
  data() {
    return {
      submitLoading: false,
      currentPasswordVerified: false,
      verifyPasswordInput: {
        label: "Current password",
        placeholder: "Type your password",
        inputType: "password",
        model: "",
        error: ""
      },
      passwordAutogenerated: true,
      customPasswordInput: {
        placeholder: "Type the custom password",
        inputType: "password",
        disabled: true,
        model: ""
      },
      //
      origin: "",
      downloadLoading: false,
      showPassword: false,
      loginDetailsOpened: false,
      loginDetails: {
        signInToken: "",
        email: "",
        status: "",
        password: ""
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    passwordHasSixChar() {
      return this.customPasswordInput.model.length >= 6;
    },
    passwordHasUppercase() {
      return /[A-Z]/.test(this.customPasswordInput.model);
    },
    passwordHasLowescase() {
      return /[a-z]/.test(this.customPasswordInput.model);
    },
    passwordHasNumber() {
      return /\d/.test(this.customPasswordInput.model);
    },
    customPasswordVerified() {
      return (
        this.passwordHasSixChar &&
        this.passwordHasUppercase &&
        this.passwordHasLowescase &&
        this.passwordHasNumber
      );
    },
    isDisabledSubmit() {
      if (!this.currentPasswordVerified) {
        if (!this.verifyPasswordInput.model) return true;
      }
      if (this.currentPasswordVerified) {
        if (!this.customPasswordVerified && !this.passwordAutogenerated) {
          return true;
        }
      }
      return false;
    }
  },
  watch: {
    passwordAutogenerated(val) {
      this.customPasswordInput.disabled = val;
    }
  },
  mounted() {
    this.origin = window.location.origin;
  },
  methods: {
    ...mapActions([
      "verifyCurrentUserPassword",
      "updateUserPassword",
      "downloadUserDetails",
      "showToast"
    ]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    async onVerifyPassword() {
      const params = {
        password: this.verifyPasswordInput.model
      };

      this.submitLoading = true;
      const { success, message } = await this.verifyCurrentUserPassword(params);
      this.submitLoading = false;
      if (!success) {
        this.verifyPasswordInput.error = message;
        this.currentPasswordVerified = false;
        return;
      }
      this.currentPasswordVerified = true;
      const title = "Password verified";
      this.showToast({ title, message: "", type: "success" });
    },
    async onSubmit() {
      const params = {
        id: this.user.id,
        user: this.passwordAutogenerated
          ? { password_auto_generated: true }
          : { password: this.customPasswordInput.model }
      };

      this.submitLoading = true;
      const { success, data } = await this.updateUserPassword(params);
      this.submitLoading = false;
      if (!success) return;

      this.loginDetails = {
        signInToken: data.user.sign_in_token,
        email: data.user.email,
        status: data.user.status,
        password: data.password
      };
      this.loginDetailsOpened = true;
    },
    //
    copyToClipboard(text, label = "Text") {
      navigator.clipboard.writeText(text);
      const title = `${label} has been copied to clipboard!`;
      this.showToast({ title, message: "", type: "success" });
    },
    async onDownloadDetails() {
      const { signInToken, email, status, password } = this.loginDetails;
      let params = `?email=${email}&password=${password}`;
      if (status === "pending") {
        params += `&sign_in_token=${signInToken}`;
      }

      this.downloadLoading = true;
      const { response, success } = await this.downloadUserDetails(params);
      this.downloadLoading = false;
      if (!success) return;

      const title = "Downloading details";
      this.showToast({ title, message: "", type: "success" });

      const href = URL.createObjectURL(response.data);
      const link = document.createElement("a");

      let filename = "";
      const disposition = response.headers["content-disposition"];
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, "");
      }

      link.href = href;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    },
    onSendViaEmail() {
      const { email } = this.loginDetails;

      const subject = `Blüm - Password reset by admin`;
      let body = `
Hello,

We hope this email finds you well. As per your request, we have reset your password for logging into the app through the admin.

Password: Your password will be provided separately by your admin.

We highly recommend changing your password to something more memorable once you log in. If you have any further questions or need assistance, please don't hesitate to contact our support team at blum@simpul.tech.
Thank you for using Blüm for your therapy sessions. We wish you continued success in your practice.


Best regards,

Blüm Support Team
`;
      body = body.replaceAll("\\\\", "%5C");
      body = body.replaceAll(" ", "%20");
      body = body.replaceAll("\r", "%0D");
      body = body.replaceAll("\n", "%0A");
      body = body.replaceAll("\t", "%09");

      const anchor = document.createElement("a");
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute(
        "href",
        `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`
      );
      anchor.click();
      setTimeout(() => {
        anchor.remove();
      }, 300);
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/team/ResetUserPasswordModal.vue